<template>
  <div class="additional-sidepanel px-4">
    <b-row class="component-bg additional-sidepanel__header d-flex align-items-center mb-2">
      <b-col class="d-flex align-items-center">
        <span class="ap-title ap-title__main">{{ $t('laboratory.tracking.moreInfo') }}</span>
      </b-col>
      <b-col
        cols="auto"
        class="d-flex justify-content-end"
      >
        <div>
          <b-button
            v-shortkey="['shift', 'esc']"
            class="btn__icon btn__icon--primary p-0"
            @shortkey.prevent="closeAdditionalSidepanel"
            @click="closeAdditionalSidepanel"
          >
            <i
              class="far fa-times"
              aria-hidden="true"
            />
          </b-button>
        </div>
      </b-col>
    </b-row>
    <!--Portion Data-->
    <b-row
      v-if="information"
      class="mt-3"
    >
      <b-col class="form-box">
        <div class="structure">
          <span
            v-if="isPortion"
            class="structure__heading"
            qa="additional-sidepanel-portion-label"
          >
            <i
              class="mr-1"
              :class="portionTypes[information.portionType.portionTypeEnum].icon"
              aria-hidden="true"
            />
            {{ information && information.portionLabel }}
          </span>
          <span
            v-if="isSubportion"
            class="structure__heading"
            qa="additional-sidepanel-subportion-label"
          >
            <i
              class="icon-porta mr-2"
              aria-hidden="true"
            />
            {{ information && information.subportionLabel }}
          </span>
          <!-- Portion -->
          <b-row v-if="isPortion">
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('component.samplesModal.type') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-sample-type"
                >
                  {{ information.sample.sampleType.description }}
                </h4>
              </div>
            </b-col>
            <b-col cols="6">
              <small>{{ $t('laboratory.content.portionType') }}</small>
              <h4
                class="collapsable-card-title inside"
                qa="additional-sidepanel-portion-type"
              >
                {{ information.portionType.potName }}
              </h4>
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('laboratory.tracking.resident') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-resident"
                >
                  {{ information.sample.caseStudy.resident ? information.sample.caseStudy.resident.fullName : '' }}
                </h4>
              </div>
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('laboratory.content.markedAsDepleted') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-marked-as-depleted"
                >
                  {{ information.depleted ? $t('global.yes') : $t('global.no') }}
                </h4>
              </div>
            </b-col>
            <b-col
              cols="12"
              class="mt-3"
            >
              <span class="ap-title ap-title__section">{{ $tc('laboratory.sidebar.processes') }}</span>
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('component.samplesModal.dateSample') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-grossing-date"
                >
                  {{ (information.sample.lastGrossingDate ? information.sample.lastGrossingDate : '-') | datetime }}
                </h4>
              </div>
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $tc('component.samplesModal.grossingResponsible') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-grossing-responsible"
                >
                  {{ information.sample.responsibleForLastGrossing ? information.sample.responsibleForLastGrossing.fullName : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col cols="12">
              <div class="horizontal-divider my-2" />
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('laboratory.grid.processingStartDate') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-processing-start-date"
                >
                  {{ (information.processingStartDate ? information.processingStartDate : '-') | datetime }}
                </h4>
              </div>
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('laboratory.grid.processingStartPersonnel') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-processing-start-personnel"
                >
                  {{ information.processingStartPersonnel ? information.processingStartPersonnel.fullName : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col cols="12">
              <div class="horizontal-divider my-2" />
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('laboratory.grid.processingEndDate') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-processing-end-date"
                >
                  {{ (information.processingEndDate ? information.processingEndDate : '-') | datetime }}
                </h4>
              </div>
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('laboratory.grid.processingEndPersonnel') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-processing-end-personnel"
                >
                  {{ information.processingEndPersonnel ? information.processingEndPersonnel.fullName : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col cols="12">
              <div class="horizontal-divider my-2" />
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('laboratory.grid.confectionDate') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-generation-date"
                >
                  {{ (information.generationDate ? information.generationDate : '-') | datetime }}
                </h4>
              </div>
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('laboratory.grid.confectionTech') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-generation-personnel"
                >
                  {{ information.generationResponsiblePersonnel ? information.generationResponsiblePersonnel.fullName : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col cols="12">
              <div class="horizontal-divider my-2" />
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('laboratory.grid.lastSubportionCutDate') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-last-subportion-cut-date"
                >
                  {{ (information && information.lastSubportionCutDate ? information.lastSubportionCutDate : '-') | datetime }}
                </h4>
              </div>
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('laboratory.grid.lastSubportionCutter') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-last-subportion-cut-personnel"
                >
                  {{ information && information.lastSubportionCutter ? information.lastSubportionCutter.fullName : '-' }}
                </h4>
              </div>
            </b-col>
          </b-row>
          <!--Subportion Data-->
          <b-row v-if="isSubportion">
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $t('component.samplesModal.type') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-sample-type"
                >
                  {{ information.portion.sample.sampleType.description }}
                </h4>
              </div>
            </b-col>
            <b-col
              v-if="!isVirtualPortionType"
              cols="6"
            >
              <div class="structure__data">
                <small>{{ $t('laboratory.content.portionType') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-portion-type"
                >
                  {{ information.portion.portionType.potName }}
                </h4>
              </div>
            </b-col>
            <b-col
              cols="12"
              class="mt-3"
            >
              <span class="ap-title ap-title__section">{{ $tc('laboratory.sidebar.processes') }}</span>
            </b-col>
            <b-col
              v-if="isVirtualPortionType"
              cols="6"
            >
              <div class="structure__data">
                <small>{{ $tc('laboratory.cytology.processingDate') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-grossing-date"
                >
                  {{ (information.portion.sample.lastGrossingDate ? information.portion.sample.lastGrossingDate : '-') | datetime }}
                </h4>
              </div>
            </b-col>
            <b-col
              v-if="isVirtualPortionType"
              cols="6"
            >
              <div class="structure__data">
                <small>{{ $tc('laboratory.grid.columns.processingResponsible') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-grossing-responsible"
                >
                  {{ information.portion.sample.responsibleForLastGrossing ? information.portion.sample.responsibleForLastGrossing.fullName : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col
              v-if="isVirtualPortionType"
              cols="12"
            >
              <div class="horizontal-divider my-2" />
            </b-col>
            <b-col
              v-if="isVirtualPortionType"
              cols="6"
            >
              <div class="structure__data">
                <small>{{ $t('laboratory.grid.processingStartDate') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-processing-start-date"
                >
                  {{ (information.portion.sample.processingStartDate ? information.portion.sample.processingStartDate : '-') | datetime }}
                </h4>
              </div>
            </b-col>
            <b-col
              v-if="isVirtualPortionType"
              cols="6"
            >
              <div class="structure__data">
                <small>{{ $tc('laboratory.grid.processingStartPersonnel') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-processing-start-personnel"
                >
                  {{ information.portion.sample.processingStartPersonnel ? information.portion.sample.processingStartPersonnel.fullName : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col
              v-if="isVirtualPortionType"
              cols="12"
            >
              <div class="horizontal-divider my-2" />
            </b-col>
            <b-col
              v-if="isVirtualPortionType"
              cols="6"
            >
              <div class="structure__data">
                <small>{{ $t('laboratory.grid.processingEndDate') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-processing-end-date"
                >
                  {{ (information.portion.sample.processingEndDate ? information.portion.sample.processingEndDate : '-') | datetime }}
                </h4>
              </div>
            </b-col>
            <b-col
              v-if="isVirtualPortionType"
              cols="6"
            >
              <div class="structure__data">
                <small>{{ $tc('laboratory.grid.processingEndPersonnel') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-processing-end-personnel"
                >
                  {{ information.portion.sample.processingEndPersonnel ? information.portion.sample.processingEndPersonnel.fullName : '-' }}
                </h4>
              </div>
            </b-col>
            <template v-if="!isVirtualPortionType">
              <b-col
                cols="6"
              >
                <div class="structure__data">
                  <small>{{ $tc('laboratory.grid.confectionDate') }}</small>
                  <h4
                    class="collapsable-card-title inside"
                    qa="additional-sidepanel-generation-date"
                  >
                    {{ (information.portion.generationDate ? information.portion.generationDate : '-') | datetime }}
                  </h4>
                </div>
              </b-col>
              <b-col
                cols="6"
              >
                <div class="structure__data">
                  <small>{{ $tc('laboratory.grid.confectionTech') }}</small>
                  <h4
                    class="collapsable-card-title inside"
                    qa="additional-sidepanel-generation-personnel"
                  >
                    {{ information.portion.generationResponsiblePersonnel ? information.portion.generationResponsiblePersonnel.fullName : '-' }}
                  </h4>
                </div>
              </b-col>
              <b-col cols="12">
                <div class="horizontal-divider my-2" />
              </b-col>
              <b-col
                cols="6"
              >
                <div class="structure__data">
                  <small>{{ $t('component.incidentModal.cutDate') }}</small>
                  <h4
                    class="collapsable-card-title inside"
                    qa="additional-sidepanel-last-subportion-cut-date"
                  >
                    {{ (information.cutDate ? information.cutDate : '-') | datetime }}
                  </h4>
                </div>
              </b-col>
              <b-col
                cols="6"
              >
                <div class="structure__data">
                  <small>{{ $tc('laboratory.grid.cutTech') }}</small>
                  <h4
                    class="collapsable-card-title inside"
                    qa="additional-sidepanel-last-subportion-cut-personnel"
                  >
                    {{ information.cutterPersonnel ? information.cutterPersonnel.fullName : '-' }}
                  </h4>
                </div>
              </b-col>
              <b-col
                cols="12"
              >
                <div class="horizontal-divider my-2" />
              </b-col>
            </template>
            <b-col cols="3">
              <div class="structure__data">
                <small>{{ $tc('laboratory.grid.stainingEntryDate') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-assay-start-date"
                >
                  {{ (information.assayStartDate ? information.assayStartDate : '-') | datetime }}
                </h4>
              </div>
            </b-col>
            <b-col cols="3">
              <div class="structure__data">
                <small>{{ $tc('global.instrument', 1) }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-assay-device"
                >
                  {{ information.assayDevice ? information.assayDevice.device : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col cols="4">
              <div class="structure__data">
                <small>{{ $tc('laboratory.grid.stainingEntryTechnician') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-assay-start-personnel"
                >
                  {{ information && information.assayStartResponsiblePersonnel ? information.assayStartResponsiblePersonnel.fullName : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col cols="12">
              <div class="horizontal-divider my-2" />
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $tc('laboratory.grid.stainingDepartureDate') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-assay-end-date"
                >
                  {{ (information.assayEndDate ? information.assayEndDate : '-') | datetime }}
                </h4>
              </div>
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $tc('laboratory.grid.stainingDepartureTechnician') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-assay-end-personnel"
                >
                  {{ information && information.assayEndResponsiblePersonnel ? information.assayEndResponsiblePersonnel.fullName : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col cols="12">
              <div class="horizontal-divider my-2" />
            </b-col>
            <b-col cols="3">
              <div
                class="structure__data"
                qa="additional-sidepanel-assay-scan-start-date"
              >
                <small>{{ $tc('laboratory.grid.scannerEntryDate') }}</small>
                <h4 class="collapsable-card-title inside">
                  {{ (information.scanStartDate ? information.scanStartDate : '-') | datetime }}
                </h4>
              </div>
            </b-col>
            <b-col cols="3">
              <div class="structure__data">
                <small>{{ $tc('global.instrument', 1) }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-scanner-device"
                >
                  {{ information.scannerDevice ? information.scannerDevice.device : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col cols="4">
              <div class="structure__data">
                <small>{{ $tc('laboratory.grid.scannerEntryTechnician') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-scan-start-personnel"
                >
                  {{ information && information.scanStartResponsiblePersonnel ? information.scanStartResponsiblePersonnel.fullName : '-' }}
                </h4>
              </div>
            </b-col>
            <b-col cols="12">
              <div class="horizontal-divider my-2" />
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $tc('laboratory.grid.scannerDepartureDate') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-scan-end-date"
                >
                  {{ (information.scanEndDate ? information.scanEndDate : '-') | datetime }}
                </h4>
              </div>
            </b-col>
            <b-col cols="6">
              <div class="structure__data">
                <small>{{ $tc('laboratory.grid.scannerDepartureTechnician') }}</small>
                <h4
                  class="collapsable-card-title inside"
                  qa="additional-sidepanel-scan-end-personnel"
                >
                  {{ information && information.scanEndResponsiblePersonnel ? information.scanEndResponsiblePersonnel.fullName : '-' }}
                </h4>
              </div>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>

    <!--Observations-->
    <b-row v-if="information">
      <b-col class="form-box">
        <div class="structure">
          <span class="structure__heading">
            <i
              class="fal fa-comment-lines mr-2"
              aria-hidden="true"
            />
            {{ $t('global.observations') }}
          </span>

          <b-row>
            <b-col>
              <div
                v-if="!information.notes && !observationBox"
                class="d-flex justify-content-between align-items-center"
                qa="additional-sidepanel-observations"
              >
                <div>
                  -
                </div>
                <b-button
                  v-if="canEditShownElement"
                  class="btn__icon btn__icon--primary px-0"
                  qa="additional-sidepanel-observations-plus-button"
                  @click="observationBox = true"
                >
                  + {{ $t('global.observations') }}
                </b-button>
              </div>
              <div
                v-if="information.notes && !observationBox"
                class="d-flex observation-edition align-items-start"
                qa="additional-sidepanel-observations"
              >
                <h4 class="collapsable-card-title inside">
                  {{ information.notes }}
                </h4>
                <b-button
                  v-if="canEditShownElement"
                  class="edit-hover btn__icon btn__icon--primary p-0"
                  qa="additional-sidepanel-observations-edit-button"
                  @click="observationBox = !observationBox"
                >
                  <i
                    class="far fa-pen ml-2"
                    aria-hidden="true"
                  />
                </b-button>
              </div>
              <div v-if="observationBox">
                <b-form-textarea
                  v-model="informationNotes"
                  size="sm"
                  rows="5"
                  qa="additional-sidepanel-observations-text-area"
                />
                <div class="d-flex justify-content-end">
                  <b-button
                    class="btn__icon btn__icon--primary ml-1 px-1"
                    qa="additional-sidepanel-observations-check-button"
                    @click="onObservationsUpdated"
                  >
                    <i
                      class="far fa-check"
                      aria-hidden="true"
                    />
                  </b-button>
                  <b-button
                    class="btn__icon btn__icon--primary px-0"
                    qa="additional-sidepanel-observations-close-button"
                    @click="onObservationsClosed"
                  >
                    <i
                      class="far fa-times"
                      aria-hidden="true"
                    />
                  </b-button>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import portionServices from '@/api/resources/common/commonPortionServices'
import subportionServices from '@/api/resources/common/commonSubportionServices'
import { PORTION_TYPES } from '@/api/resources/portionTypeService'
import laboratoryDynamicServices from '@/modules/laboratoryModule/services/laboratoryDynamicServices'
import { PortionOperationGuardMixin } from '@/api/operations/mixins/guards/portion-operation-guard.mixin'
import { SubportionOperationGuardMixin } from '@/api/operations/mixins/guards/subportion-operation-guard.mixin'

export default {
    name: 'AdditionalSidepanel',
    mixins: [
        PortionOperationGuardMixin,
        SubportionOperationGuardMixin
    ],
    data() {
        return {
            information: null,
            informationNotes: null,
            portionTypes: PORTION_TYPES,
            observationBox: false
        }
    },
    computed: {
        labId() {
            return this.$store.getters.labId
        },
        isVirtualPortionType() {
            return this.information.portion.portionType.portionTypeEnum === PORTION_TYPES.VIRTUAL.key
        },
        entitySubtypeName() {
            if (this.isPortion) {
                return this.information.portionType.potName
            }
            if (this.isSubportion) {
                return this.information.subportionType.sptName
            }
            return null
        },
        isPortion() {
            return !!(this.information && this.information.portionType)
        },
        isSubportion() {
            return !!(this.information && this.information.subportionType)
        },
        canEditShownElement() {
            if (this.isPortion) {
                return this.$_canEditPortion()
            }
            if (this.isSubportion) {
                return this.$_canEditSubportion()
            }
            return false
        }
    },
    methods: {
        closeAdditionalSidepanel() {
            this.$emit('closeAdditionalSidepanel', false)
        },
        loadPortionInformation(data) {
            this.internalLoadPortionInformation(this.labId, data.sample.caseStudy.casId, data.sample.samId, data.porId)
        },
        async internalLoadPortionInformation(laboratoryId, caseId, sampleId, portionId) {
            this.information = null
            const [err, response] = await this.toCall(portionServices.getPortionInformation(laboratoryId, caseId, sampleId, portionId))
            if (err) {
                this.showErrorModalGlobal()
                this.closeAdditionalSidepanel()
                return
            }
            this.information = response
            this.informationNotes = this.information.notes
        },
        loadSubportionInformation(data) {
            this.internalLoadSubportionInformation(this.labId, data.portion.sample.caseStudy.casId, data.portion.sample.samId, data.portion.porId, data.supId)
        },
        async internalLoadSubportionInformation(laboratoryId, caseId, sampleId, portionId, subportionId) {
            this.information = null
            const [err, response] = await this.toCall(subportionServices.getSubportionInformation(laboratoryId, caseId, sampleId, portionId, subportionId))
            if (err) {
                this.showErrorModalGlobal()
                this.closeAdditionalSidepanel()
                return
            }
            this.information = response
            this.informationNotes = this.information.notes
        },
        onObservationsClosed() {
            this.observationBox = false
            this.informationNotes = this.information.notes
        },
        onObservationsUpdated() {
            if (this.isPortion) {
                this.updateObservations(laboratoryDynamicServices.patchPortion, this.internalLoadPortionInformation,
                    [this.information.sample.caseStudy.id, this.information.sample.id, this.information.id],
                    {
                        originalConcurrencyUpdatedAt: this.information.originalConcurrencyUpdatedAt,
                        notes: this.informationNotes
                    })
            } else {
                this.updateObservations(laboratoryDynamicServices.patchSubportion, this.internalLoadSubportionInformation,
                    [this.information.portion.sample.caseStudy.id, this.information.portion.sample.id, this.information.portion.id, this.information.id],
                    {
                        originalConcurrencyUpdatedAt: this.information.originalConcurrencyUpdatedAt,
                        observations: this.informationNotes
                    })
            }
        },
        async updateObservations(patchFunction, getFunction, args, request) {
            this.updateLoading(true)
            const [error] = await this.toCall(patchFunction.call(laboratoryDynamicServices, this.labId, ...args, request))
            this.updateLoading(false)
            if (error) {
                if (error.response && error.response.data && error.response.data.code === this.$apiExceptions.CONCURRENCY_ERROR) {
                    return this.processBasicConcurrencyConflict(this.entitySubtypeName, error, () => {
                        this.information.originalConcurrencyUpdatedAt = error.response.data.currentConcurrencyUpdatedAt
                        return this.onObservationsUpdated()
                    })
                } else {
                    this.showErrorModalGlobalFromErrorResponse(error)
                    return false
                }
            } else {
                this.showToastSuccessGlobal()
                await getFunction(this.labId, ...args)
                this.observationBox = false
            }
        }
    }
}
</script>

<style lang="scss">
    .observation-edition:hover .edit-hover {
        display: block;
    }

    .edit-hover {
        display: none;
    }

    .observation-edition h4 {
        margin-top: 2px;
    }
</style>

<style lang="scss" scoped>
    .additional-sidepanel {
        height: 100%;
        overflow-y: auto;
        .additional-sidepanel__header {
            position: sticky;
            top: 0;
            z-index: 1;
        }
    }
</style>
